import { Link } from 'react-router-dom';
import { Check, ExternalLink } from 'lucide-react';
import { useInView } from '@/hooks/useInView';

function RevealSection({ children, className = '', delay = 0 }) {
  const [ref, isInView] = useInView();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${className}`}
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0)' : 'translateY(24px)',
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

export default function ThankYou() {
  // REPLACE THIS with your real Notion template link
  const notionTemplateUrl = 'https://false-owner-dd4.notion.site/High-Stakes-Narrative-Pack-King-Makers-339dd4b14fbf813cba6eefb2931e258d?source=copy_link';

  return (
    <div data-testid="thank-you-page" className="px-6 lg:px-8 pt-[80px] pb-[100px] md:pt-[120px] md:pb-[120px]">
      <div className="max-w-2xl mx-auto">
        <RevealSection>
          <div className="w-16 h-16 rounded-full bg-km-gold-gradient shadow-km-gold-glow flex items-center justify-center mx-auto mb-8">
            <Check className="w-8 h-8 text-km-gray-1" />
          </div>
          <h1 className="font-inter font-bold text-[30px] md:text-[44px] leading-[1.1] tracking-tight text-km-white text-center">
            You're in. Here's your High-Stakes Narrative Pack
          </h1>
          <p className="mt-4 font-inter font-medium text-base md:text-lg text-km-gray-5 leading-[1.3] text-center">
            Thanks for your purchase. Below is your instant access link and the fastest way to start.
          </p>
        </RevealSection>

        <RevealSection delay={150}>
          <div className="mt-14 p-8 md:p-10 rounded-[10px] bg-km-card-gradient border-2 border-km-gold/30 shadow-km-gold-soft">
            <h2 className="font-inter font-medium text-[20px] md:text-[24px] leading-[1.2] text-km-white mb-8">
              Access the Notion Toolkit
            </h2>

            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-km-gold/10 border border-km-gold/30 flex items-center justify-center font-inter font-bold text-sm text-km-gold">1</span>
                <div>
                  <p className="font-inter font-medium text-km-white">Open the Notion template</p>
                  <a
                    href={notionTemplateUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="notion-template-link"
                    className="mt-2 inline-flex items-center gap-2 font-inter font-medium text-km-gold hover:text-km-gold-deep transition-colors cursor-pointer"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Access your High-Stakes Narrative Pack (Notion Template)
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-km-gold/10 border border-km-gold/30 flex items-center justify-center font-inter font-bold text-sm text-km-gold">2</span>
                <div>
                  <p className="font-inter font-medium text-km-white mb-3">Duplicate it to your workspace</p>
                  <ul className="space-y-2">
                    <li className="font-inter font-medium text-sm text-km-gray-5 flex items-start gap-2">
                      <span className="text-km-gold mt-0.5">&bull;</span> Open the link above in Notion
                    </li>
                    <li className="font-inter font-medium text-sm text-km-gray-5 flex items-start gap-2">
                      <span className="text-km-gold mt-0.5">&bull;</span> Click <strong className="font-semibold text-km-white">Duplicate</strong> (top-right)
                    </li>
                    <li className="font-inter font-medium text-sm text-km-gray-5 flex items-start gap-2">
                      <span className="text-km-gold mt-0.5">&bull;</span> Choose the workspace where you want to keep it
                    </li>
                  </ul>
                  <p className="mt-3 font-inter font-medium text-xs text-km-gray-5">
                    If you don't see "Duplicate," make sure you're logged into Notion first.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </RevealSection>

        <RevealSection delay={250}>
          <div className="mt-8 p-8 md:p-10 rounded-[10px] bg-km-card-gradient border border-km-gray-3">
            <h2 className="font-inter font-medium text-[20px] md:text-[24px] leading-[1.2] text-km-white mb-4">
              Start here (60-minute quickstart)
            </h2>
            <p className="font-inter font-medium text-base text-km-gray-5 leading-[1.3]">
              Open <strong className="font-semibold text-km-white">Start Here &rarr; How to use this pack</strong>, then follow:
            </p>
            <p className="mt-3 font-inter font-semibold text-km-gold tracking-wide">
              Frame &rarr; Build &rarr; De-risk &rarr; Deploy &rarr; Land
            </p>
          </div>
        </RevealSection>

        <RevealSection delay={350}>
          <div className="mt-8 p-6 rounded-[10px] bg-km-gray-2 border border-km-gray-3">
            <h3 className="font-inter font-medium text-km-white mb-2">Need help?</h3>
            <p className="font-inter font-medium text-sm text-km-gray-5 leading-[1.3]">
              If you have any access issues, email:{' '}
              <a href="mailto:hello@kingmakersinc.ca" className="text-km-gold hover:text-km-gold-deep transition-colors cursor-pointer">hello@kingmakersinc.ca</a>
            </p>
            <p className="font-inter font-medium text-sm text-km-gray-5 mt-1">
              (Include your receipt email and we'll help quickly.)
            </p>
          </div>
        </RevealSection>

        <RevealSection delay={400}>
          <p className="mt-8 font-inter font-medium text-xs text-km-gray-5 text-center">
            You'll receive any major updates to this toolkit via email.
          </p>
        </RevealSection>

        <RevealSection delay={450}>
          <div className="mt-10 text-center">
            <Link to="/" data-testid="back-to-home" className="font-inter font-medium text-sm text-km-gray-5 hover:text-km-white transition-colors cursor-pointer">
              &larr; Back to King Makers
            </Link>
          </div>
        </RevealSection>
      </div>
    </div>
  );
}
